import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Combobox,
  ComboboxOption,
  ComboboxGroup,
  TextField,
  MenuItem,
  makeStyles,
  Theme,
  Divider,
  Typography,
} from '@bb-ui/react-library';
import { PrimaryButton } from '@bb-ui/react-library/dist/components';
import useRestApi from 'hooks/useRestApi';
import { useAppConfigContext } from 'contexts/AppConfigContext';
import { apiUrl } from 'utils/apiUrl';
import { ErrorMessage } from 'components/ErrorMessage';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { PageTemplate } from 'components/PageTemplate';
import { MassQueryToolingTable } from './MassQueryToolingTable';
import { MassQueryToolingSelectedSites } from './MassQueryToolingSelectedSites';
import { MassQueryToolingAgreementDialog } from './MassQueryToolingAgreementDialog';
import { MassQueryForm } from './MassQueryForm';
import { MassJobForm } from './MassJobForm';
import { LearnInstanceItem } from './MassQueryTooling.types';
import { useAllClientsMap } from 'pages/LearnClients/useAllClientsMap';
import { Switch } from '@bb-ui/react-library/dist/components/Switch';
import { TriangleLeft, TriangleRight } from '@bb-ui/icons/dist/small';
import { useHistory } from 'react-router';
import { read as readXlsx, utils as xlsxUtils } from 'xlsx';
import { useSnackbar } from 'hooks/useSnackbar';

const useStyles = makeStyles((theme: Theme) => ({
  filtersContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  regionSelect: {
    minWidth: 180,
    backgroundColor: theme.palette.common.white,
  },
  comboboxInput: {
    backgroundColor: theme.palette.common.white,
  },
  comboboxWrapper: {
    flexGrow: 1,
    flexBasis: 480,
    minWidth: 240,
    maxWidth: 720,
  },
  runButton: {
    whiteSpace: 'nowrap',
  },
  formSwitchContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
  },
  formSwitchDivider: {
    flexGrow: 1,
  },
  formSwitchLabel: {
    fontWeight: 500,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  formSwitchLabelActive: {
    color: theme.palette.primary.main,
  },
  formSwitchLabelInactive: {
    color: theme.palette.text.disabled,
    opacity: 0.5,
  },
  switchIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.common.white,
  },
  contentWrapper: {
    position: 'relative',
  },
  dragOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: `${theme.palette.primary.light}15`,
    border: `3px dashed ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius * 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    pointerEvents: 'none',
  },
  dragOverlayText: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
}));

export const MassQueryTooling: React.FunctionComponent = () => {
  type Option = ComboboxOption;
  type Group = ComboboxGroup<Option>;
  const { t } = useTranslation();
  const env = useAppConfigContext();
  const classes = useStyles();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const regions = env.houstonSupportedRegions ?? ['us-east-1'];

  const [hasAgreed, setHasAgreed] = React.useState(false);
  const [selectedRegion, setSelectedRegion] = React.useState<string>(regions[0]);
  const [selectedOptions, setSelectedOptions] = React.useState<Option[]>([]);
  const [selectedSites, setSelectedSites] = React.useState<LearnInstanceItem[]>([]);
  const [formMode, setFormMode] = React.useState<'query' | 'job'>('query');
  const [allInstances, setAllInstances] = React.useState<LearnInstanceItem[]>([]);
  const [initialLoadComplete, setInitialLoadComplete] = React.useState(false);
  const currentRegionRef = React.useRef(selectedRegion);
  const fetchedTokensRef = React.useRef<Set<string>>(new Set());
  const [isDragging, setIsDragging] = React.useState(false);
  const dragCounterRef = React.useRef(0);
  const [paginationUrl, setPaginationUrl] = React.useState<string>(
    apiUrl('lct', `regions/${selectedRegion}/instances/learnInstances`),
  );

  const {
    data: learnInstanceData,
    loading: learnInstanceLoading,
    error: learnInstanceError,
  } = useRestApi(paginationUrl);

  const { clientsMap, loading: clientsLoading, error: clientsError } = useAllClientsMap();

  React.useEffect(() => {
    if (!learnInstanceData || learnInstanceError) return;

    const { items = [], nextPageToken } = learnInstanceData;

    if (items.length > 0) {
      setAllInstances((prev) => {
        const existingIds = new Set(prev.map(({ TenantId }) => TenantId));
        const newItems = items.filter(
          ({ TenantId }: { TenantId: string }) => !existingIds.has(TenantId),
        );
        return [...prev, ...newItems];
      });
    }

    if (!nextPageToken) {
      if (!initialLoadComplete) {
        setInitialLoadComplete(true);
      }
      return;
    }

    if (!fetchedTokensRef.current.has(nextPageToken)) {
      fetchedTokensRef.current.add(nextPageToken);
      setPaginationUrl(
        apiUrl(
          'lct',
          `regions/${currentRegionRef.current}/instances/learnInstances?nextPageToken=${encodeURIComponent(nextPageToken)}`,
        ),
      );
    }
  }, [learnInstanceData, learnInstanceError, initialLoadComplete]);

  React.useEffect(() => {
    if (currentRegionRef.current !== selectedRegion) {
      setAllInstances([]);
      setInitialLoadComplete(false);
      fetchedTokensRef.current.clear();
      setPaginationUrl(apiUrl('lct', `regions/${selectedRegion}/instances/learnInstances`));
      currentRegionRef.current = selectedRegion;
    }
  }, [selectedRegion]);

  const filteredInstances = React.useMemo(
    () =>
      allInstances.filter((item: LearnInstanceItem) => {
        if (item.Region && item.Region !== selectedRegion) return false;

        if (item.Status !== 'Installed') return false;
        if (item.FleetId?.toLowerCase() === 'legacy') return false;
        const client = item.ClientId ? clientsMap.get(item.ClientId) : undefined;

        const clientName = client?.name ?? '';
        if (clientName.startsWith('Integration Test Client:')) return false;
        return true;
      }),
    [allInstances, clientsMap, selectedRegion],
  );

  const strings = {
    announceOptionSelected: (option: { label: any }) => `${option.label}, selected.`,
    announceOptionDeselected: (option: { label: any }) => `${option.label}, deselected.`,
    announceValueCleared: 'All values deselected.',
    announceSearchResults: (count: any, searchString: any) => {
      switch (count) {
        case 0:
          return `No results found for "${searchString}"`;
        case 1:
          return `${count} result found for "${searchString}". Press down arrow to navigate through the results.`;
        default:
          return `${count} results found for "${searchString}". Press down arrow to navigate through the results.`;
      }
    },
    noResults: (searchString: any) => `No results found for "${searchString}"`,
    clearButtonLabel: 'Deselect all values.',
    searchLabel: 'Search',
    emptyListLabel: 'Type to search…',
  };

  const uniqueClientNames = React.useMemo<string[]>(
    () =>
      Array.from(
        new Set(
          filteredInstances
            .map((site: LearnInstanceItem) => {
              const client = site.ClientId ? clientsMap.get(site.ClientId) : undefined;
              return client?.name;
            })
            .filter((v: unknown): v is string => Boolean(v && typeof v === 'string')),
        ),
      ),
    [filteredInstances, clientsMap],
  );

  const uniqueFleetIds = React.useMemo<string[]>(
    () =>
      Array.from(
        new Set(
          filteredInstances
            .map((site: LearnInstanceItem) => site.FleetId)
            .filter((v: unknown): v is string => Boolean(v && typeof v === 'string')),
        ),
      ),
    [filteredInstances],
  );

  const uniqueInstallationIds = React.useMemo<string[]>(
    () =>
      Array.from(
        new Set(
          filteredInstances
            .map((site: LearnInstanceItem) => site.InstallationId)
            .filter((v: unknown): v is string => Boolean(v && typeof v === 'string')),
        ),
      ),
    [filteredInstances],
  );

  const uniqueUrls = React.useMemo<string[]>(
    () =>
      Array.from(
        new Set(
          filteredInstances
            .map((site: LearnInstanceItem) => site.Url)
            .filter((v: unknown): v is string => Boolean(v && typeof v === 'string' && v.trim())),
        ),
      ),
    [filteredInstances],
  );

  const uniqueTenantIds = React.useMemo<string[]>(
    () =>
      Array.from(
        new Set(
          filteredInstances
            .map((site: LearnInstanceItem) => site.TenantId)
            .filter((v: unknown): v is string => Boolean(v && typeof v === 'string')),
        ),
      ),
    [filteredInstances],
  );

  const uniqueLearnVersions = React.useMemo<string[]>(
    () =>
      Array.from(
        new Set(
          filteredInstances
            .map((site: LearnInstanceItem) => site.LearnVersion)
            .filter((v: unknown): v is string => Boolean(v && typeof v === 'string')),
        ),
      ),
    [filteredInstances],
  );

  const groupedOptions = React.useMemo<(Option | Group)[]>(
    () => [
      {
        label: 'Fleet ID',
        options: uniqueFleetIds.map<Option>((fleetId) => ({
          value: fleetId,
          label: fleetId,
        })),
      },
      {
        label: 'Learn Version',
        options: uniqueLearnVersions.map<Option>((learnVersion) => ({
          value: learnVersion,
          label: learnVersion,
        })),
      },
      {
        label: 'Client Name',
        options: uniqueClientNames.map<Option>((name) => ({
          value: name as string,
          label: name as string,
        })),
      },
      {
        label: 'Url',
        options: uniqueUrls.map<Option>((url) => ({
          value: url,
          label: url,
        })),
      },

      {
        label: 'Installation ID',
        options: uniqueInstallationIds.map<Option>((id) => ({
          value: id,
          label: id,
        })),
      },

      {
        label: 'Tenant ID',
        options: uniqueTenantIds.map<Option>((id) => ({
          value: id,
          label: id,
        })),
      },
    ],
    [
      uniqueUrls,
      uniqueClientNames,
      uniqueInstallationIds,
      uniqueFleetIds,
      uniqueLearnVersions,
      uniqueTenantIds,
    ],
  );

  const filteredSites = React.useMemo(() => {
    if (selectedOptions.length === 0) return filteredInstances;

    const selectedValues = selectedOptions
      .map((opt) => opt.value?.toString().toLowerCase() ?? '')
      .filter((v) => v.trim() !== '');

    if (selectedValues.length === 0) return filteredInstances;

    return filteredInstances.filter((site: LearnInstanceItem) => {
      const client = site.ClientId ? clientsMap.get(site.ClientId) : undefined;

      const clientName = client?.name ?? '';

      const fields = [
        site.Url || '',
        site.InstallationId || '',
        site.TenantId || '',
        clientName,
        site.FleetId || '',
        site.LearnVersion || '',
      ].map((field) => field.toLowerCase());

      return selectedValues.some((value) => fields.some((field) => field.includes(value)));
    });
  }, [filteredInstances, selectedOptions, clientsMap]);

  const selectedTenantIds = React.useMemo(
    () => new Set(selectedSites.map((s) => s.TenantId).filter((id): id is string => Boolean(id))),
    [selectedSites],
  );

  const handleRegionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedRegion(e.target.value);
    setSelectedOptions([]);
    setSelectedSites([]);
  };

  const handleToggleSiteFromTable = (site: LearnInstanceItem) => {
    if (!site.TenantId) return;
    setSelectedSites((prev) => {
      const exists = prev.some((s) => s.TenantId === site.TenantId);
      if (exists) {
        return prev.filter((s) => s.TenantId !== site.TenantId);
      }
      return [...prev, site];
    });
  };

  const handleToggleAllFromTable = () => {
    const tenantIdsInPage = filteredSites
      .map((s: { TenantId: any }) => s.TenantId)
      .filter((id: any): id is string => Boolean(id));

    if (tenantIdsInPage.length === 0) return;

    setSelectedSites((prev) => {
      const currentIds = new Set(
        prev.map((s) => s.TenantId).filter((id): id is string => Boolean(id)),
      );

      const allSelected =
        tenantIdsInPage.length > 0 && tenantIdsInPage.every((id: string) => currentIds.has(id));

      if (allSelected) {
        return prev.filter((s) => !s.TenantId || !tenantIdsInPage.includes(s.TenantId));
      }

      const next = [...prev];
      const nextIds = new Set(currentIds);

      filteredSites.forEach((site: LearnInstanceItem) => {
        if (!site.TenantId) return;
        if (!nextIds.has(site.TenantId)) {
          next.push(site);
          nextIds.add(site.TenantId);
        }
      });

      return next;
    });
  };

  const handleCsvImport = (importedIds: string[]) => {
    const byTenantId = new Map<string, LearnInstanceItem>();
    const byInstallationId = new Map<string, LearnInstanceItem>();

    filteredInstances.forEach((site: LearnInstanceItem) => {
      if (site.TenantId) {
        byTenantId.set(site.TenantId, site);
      }
      if (site.InstallationId) {
        byInstallationId.set(site.InstallationId, site);
      }
    });

    setSelectedSites((prev) => {
      const next = [...prev];
      const existing = new Set(
        prev.map((s) => s.TenantId).filter((id): id is string => Boolean(id)),
      );

      importedIds.forEach((id) => {
        const trimmed = id.trim();
        if (!trimmed) return;

        const site = byTenantId.get(trimmed) || byInstallationId.get(trimmed);
        if (site && site.TenantId && !existing.has(site.TenantId)) {
          next.push(site);
          existing.add(site.TenantId);
        }
      });

      return next;
    });
  };

  const handleRemoveSelected = (tenantId: string) => {
    setSelectedSites((prev) => prev.filter((s) => s.TenantId !== tenantId));
  };

  const findTenantIdColumn = (headers: string[]): number => {
    const normalized = headers.map((h) =>
      h
        .trim()
        .toLowerCase()
        .replace(/[\s_-]/g, ''),
    );

    const tenantIdIndex = normalized.indexOf('tenantid');
    if (tenantIdIndex !== -1) return tenantIdIndex;

    const installationIdIndex = normalized.indexOf('installationid');
    return installationIdIndex;
  };

  const parseCsvText = (text: string): string[] => {
    const lines = text
      .split(/\r?\n/)
      .map((r) => r.trim())
      .filter(Boolean);

    if (lines.length === 0) return [];

    const headers = lines[0].split(',').map((h) => h.trim());
    const colIndex = findTenantIdColumn(headers);

    if (colIndex === -1) return lines.slice(1);

    return lines
      .slice(1)
      .map((line) => {
        const cols = line.split(',');
        return (cols[colIndex] ?? '').trim();
      })
      .filter(Boolean);
  };

  const parseExcelBuffer = (buffer: ArrayBuffer): string[] => {
    const workbook = readXlsx(buffer, { type: 'array' });
    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows: string[][] = xlsxUtils.sheet_to_json(firstSheet, { header: 1 });

    if (rows.length === 0) return [];

    const headers = rows[0].map(String);
    const colIndex = findTenantIdColumn(headers);

    if (colIndex === -1) {
      return rows
        .slice(1)
        .map((row) => String(row[0] ?? '').trim())
        .filter(Boolean);
    }

    return rows
      .slice(1)
      .map((row) => String(row[colIndex] ?? '').trim())
      .filter(Boolean);
  };

  const processImportedIds = (importedIds: string[]) => {
    const validTenantIdsSet = new Set(filteredInstances.map((s) => s.TenantId));
    const validInstallationIdsSet = new Set(filteredInstances.map((s) => s.InstallationId));

    const validIds = importedIds.filter(
      (id) => validTenantIdsSet.has(id) || validInstallationIdsSet.has(id),
    );

    const currentlySelectedTenantIds = new Set(selectedSites.map((s) => s.TenantId));
    const byTenantId = new Map(filteredInstances.map((s) => [s.TenantId, s]));
    const byInstallationId = new Map(filteredInstances.map((s) => [s.InstallationId, s]));

    const newlyAddedTenantIds = new Set<string>();
    validIds.forEach((id) => {
      const site = byTenantId.get(id) || byInstallationId.get(id);
      if (site && site.TenantId && !currentlySelectedTenantIds.has(site.TenantId)) {
        newlyAddedTenantIds.add(site.TenantId);
      }
    });

    handleCsvImport(validIds);

    if (newlyAddedTenantIds.size > 0) {
      enqueueSnackbar(t('massQueryTooling.csv.success', { count: newlyAddedTenantIds.size }), {
        variant: 'success',
      });
    } else {
      enqueueSnackbar(t('massQueryTooling.csv.noNewEntries'), { variant: 'error' });
    }
  };

  const isExcelFile = (file: File): boolean => {
    const excelExtensions = ['.xlsx', '.xls'];
    return excelExtensions.some((ext) => file.name.toLowerCase().endsWith(ext));
  };

  const processFile = (file: File) => {
    const reader = new FileReader();

    if (isExcelFile(file)) {
      reader.onload = (ev) => {
        const buffer = ev.target?.result as ArrayBuffer;
        const tenantIds = parseExcelBuffer(buffer);
        processImportedIds(tenantIds);
      };
      reader.readAsArrayBuffer(file);
    } else {
      reader.onload = (ev) => {
        const text = String(ev.target?.result ?? '');
        const tenantIds = parseCsvText(text);
        processImportedIds(tenantIds);
      };
      reader.readAsText(file);
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = dragCounterRef.current + 1;
    if (dragCounterRef.current === 1) {
      setIsDragging(true);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = dragCounterRef.current - 1;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  let content;

  if (learnInstanceError) {
    content = (
      <ErrorMessage
        data-testid="learn-instance-list-error"
        title={t('massQueryTooling.errorMessages.instances')}
        message={learnInstanceError.message}
        variant="block"
      />
    );
  } else if (clientsError) {
    content = (
      <ErrorMessage
        data-testid="learn-client-list-error"
        title={t('massQueryTooling.errorMessages.clients')}
        message={clientsError.message}
        variant="block"
      />
    );
  } else if (clientsLoading) {
    content = <LoadingIndicator data-testid="instance-list-init" />;
  } else {
    content = (
      <div
        className={classes.contentWrapper}
        data-testid="mass-query-tooling-content"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDragging && (
          <div className={classes.dragOverlay}>
            <Typography className={classes.dragOverlayText}>Drop file to import sites</Typography>
          </div>
        )}
        <MassQueryToolingTable
          sites={filteredSites}
          clientsMap={clientsMap}
          selectedTenantIds={selectedTenantIds}
          onToggleSite={handleToggleSiteFromTable}
          onToggleAll={handleToggleAllFromTable}
        />
        <MassQueryToolingSelectedSites
          allSites={filteredInstances}
          selectedSites={selectedSites}
          onRemove={handleRemoveSelected}
          onCsvImport={handleCsvImport}
          processFile={processFile}
        />
        <div className={classes.formSwitchContainer}>
          <Divider className={classes.formSwitchDivider} />
          <span
            className={`${classes.formSwitchLabel} ${formMode === 'query' ? classes.formSwitchLabelActive : classes.formSwitchLabelInactive}`}
          >
            Run Mass Query
          </span>
          <Switch
            checked={formMode === 'job'}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFormMode(e.target.checked ? 'job' : 'query')
            }
            color="primary"
            icon={
              <div className={classes.switchIcon}>
                <TriangleLeft />
              </div>
            }
            checkedIcon={
              <div className={classes.switchIcon}>
                <TriangleRight />
              </div>
            }
          />
          <span
            className={`${classes.formSwitchLabel} ${formMode === 'job' ? classes.formSwitchLabelActive : classes.formSwitchLabelInactive}`}
          >
            Run Mass Job
          </span>
          <Divider className={classes.formSwitchDivider} />
        </div>
        {formMode === 'query' ? (
          <MassQueryForm
            selectedSites={selectedSites}
            selectedRegion={selectedRegion}
            setSelectedSites={(sites) => {
              setSelectedSites(sites as LearnInstanceItem[]);
            }}
            onSuccess={() => setSelectedSites([])}
          />
        ) : (
          <MassJobForm selectedRegion={selectedRegion} selectedSites={selectedSites} />
        )}
      </div>
    );
  }

  return (
    <PageTemplate title={t('massQueryTooling.tabTitle')} data-testid="mass-query-tooling-page">
      <MassQueryToolingAgreementDialog open={!hasAgreed} onAgree={() => setHasAgreed(true)} />
      <div className={classes.filtersContainer}>
        <TextField
          select
          id="region-select"
          label={t('massQueryTooling.selectRegionLabel')}
          value={selectedRegion}
          onChange={handleRegionChange}
          className={classes.regionSelect}
          floatingLabel
        >
          {regions.map((region) => (
            <MenuItem key={region} value={region}>
              {region}
            </MenuItem>
          ))}
        </TextField>

        <div className={classes.comboboxWrapper}>
          <Combobox
            id="search-field-combobox"
            label={t('massQueryTooling.search.title')}
            placeholder={t('massQueryTooling.search.placeholder')}
            strings={strings}
            options={groupedOptions}
            isMulti
            value={selectedOptions}
            onChange={(value) => {
              setSelectedOptions(value);
            }}
            isClearable
            fullWidth
            InputProps={{
              classes: { root: classes.comboboxInput },
            }}
          />
        </div>
        {learnInstanceLoading && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              alignSelf: 'flex-end',
              gap: '8px',
              whiteSpace: 'nowrap',
              marginBottom: '8px',
            }}
          >
            <LoadingIndicator size="small" />
            <span style={{ fontSize: '13px', color: 'rgba(0, 0, 0, 0.6)' }}>
              {t('massQueryTooling.loadingMoreSites', {
                count: filteredInstances.length,
                defaultValue: `Loading more sites... (${filteredInstances.length} loaded so far)`,
              })}
            </span>
          </div>
        )}

        <div style={{ flexGrow: 1 }} />

        <PrimaryButton
          data-testid="view-executions-button"
          onClick={() => history.push('/query-tooling/executions')}
          className={classes.runButton}
        >
          {t('massQueryTooling.executions.viewExecutions')}
        </PrimaryButton>
      </div>

      {content}
    </PageTemplate>
  );
};
